'use client';

import React, { useEffect, useState } from 'react';

import Image from 'next/image';
import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { stegaClean } from '@sanity/client/stega';

import Chip from '@/components/global/ui/Chip';
import DecorationLine from '@/components/global/ui/DecorationLine';
import { HLSVideoPlayer } from '@/components/ui/SanityVideoPlayer';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/shadcn/ui/carousel';

import { PageCareerSchema } from '@/sanity/pages/PageCareerTemplate';

import chevron from '@/public/iq-capital-launch/chevron-right.svg';

/*
  The chevron artwork is masked rather than rendered as an <img> so each button can
  paint it its own colour — dark on the light previous button, white on the dark
  next one — from the one asset the design system already ships.
*/
const CHEVRON_MASK = {
  maskImage: `url(${chevron.src})`,
  WebkitMaskImage: `url(${chevron.src})`,
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
} as const;

/*
  The controls move between two places in the design — beside the heading on
  desktop, on their own full-width bar under the carousel on mobile — so they live
  in one component that gets rendered in both slots rather than being duplicated.
  The pill is a stack of rings: fill, a white gap, then a hairline.
*/
function CarouselArrows({
  api,
  canPrev,
  canNext,
  className,
}: {
  api?: CarouselApi;
  canPrev: boolean;
  canNext: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex rounded-full bg-[#F3F2F1] p-[1.5rem] ring-1 ring-[#D3D3D2] ring-offset-[0.25rem] ring-offset-white',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => api?.scrollPrev()}
        disabled={!canPrev}
        aria-label="Vorherige"
        className="flex h-[7rem] w-[7rem] items-center justify-center rounded-full bg-[#202223]/10 transition-opacity duration-300 disabled:opacity-40"
      >
        <span
          className="h-[4rem] w-[4rem] -scale-x-100 bg-[#202223]"
          style={CHEVRON_MASK}
        />
      </button>

      <button
        type="button"
        onClick={() => api?.scrollNext()}
        disabled={!canNext}
        aria-label="Nächste"
        className="flex h-[7rem] w-[7rem] items-center justify-center rounded-full bg-[#202223] transition-opacity duration-300 disabled:opacity-40"
      >
        <span className="h-[4rem] w-[4rem] bg-white" style={CHEVRON_MASK} />
      </button>
    </div>
  );
}

export default function CultureSection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const items = data?.cultureItems;
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  if (!items?.length) return null;

  return (
    <section
      className={cn(
        'relative overflow-clip pb-[4.375rem] pt-[7.5rem] sm:pb-[18.75rem] sm:pt-[14.5rem]',
        className,
      )}
    >
      {/* header — ranged centre on mobile; from lg the arrows sit on its baseline, as drawn */}
      <div className="mx-auto flex w-[min(187.75rem,100%)] flex-col gap-[4rem] px-[3rem] md:px-[5rem] lg:flex-row lg:items-end lg:justify-between lg:gap-0 lg:px-0 relative">
        <div className="flex flex-col items-center gap-[2rem] sm:items-start sm:gap-[1rem] lg:w-[168.75rem]">
          {data?.cultureChip?.text && (
            <Chip
              className="mb-0 [&_p]:font-geistmono [&_p]:uppercase [&_p]:text-[#202223]"
              image={data?.cultureChip?.icon}
              text={data?.cultureChip?.text}
            />
          )}

          {data?.cultureTitle && (
            <div className="pixel-highlight richtext richtext-center prose-headings:mb-[2rem] prose-em:font-ivy prose-em:!px-0 sm:richtext-start sm:prose-headings:mb-[2.25rem] sm:prose-p:w-[min(62rem,100%)] lg:prose-em:!px-[.7rem] prose-p:w-[min(44.5rem,100%)]">
              <PortableText
                value={data.cultureTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <CarouselArrows
          className="hidden shrink-0 gap-[1rem] self-start sm:flex lg:self-end"
          api={api}
          canPrev={canPrev}
          canNext={canNext}
        />
      </div>

      {/* divider, centred in the gap between header and cards */}
      <div className="relative mx-auto mt-[3.125rem] h-[2px] w-[min(225.375rem,100%)] sm:mt-[6.25rem]">
        <DecorationLine
          className="top-0"
          orientation="horizontal"
          style="dark"
        />
      </div>

      <div className="mx-auto mt-[2.125rem] w-[min(187.75rem,100%)] px-[3rem] sm:mt-[6.25rem] md:px-[5rem] lg:px-0">
        <Carousel setApi={setApi} opts={{ align: 'start' }}>
          {/*
            the slides run past the carousel here and are clipped by the section
            instead, so this one viewport opts out of embla's own clip
          */}
          <CarouselContent
            className="-ml-[0.75rem] sm:-ml-[1rem]"
            viewportClassName="overflow-visible"
          >
            {items.map((item, index) => {
              const isVideo = stegaClean(item?.mediaType) === 'video';
              const hasVideo =
                item?.video?.muxVideo?.playbackId || item?.video?.embedUrl;
              const hasMedia = isVideo ? hasVideo : item?.image?.src;

              return (
                <CarouselItem
                  key={index}
                  className="basis-auto pl-[0.75rem] sm:pl-[1rem]"
                >
                  <div className="flex w-[41.5rem] flex-col gap-[2.25rem] sm:w-[min(56rem,80vw)] sm:gap-[3rem]">
                    {hasMedia && (
                      <div className="relative aspect-[332/390] w-full overflow-clip sm:aspect-[448/530]">
                        {isVideo ? (
                          /* autoplaying muted loop — the card reads as a moving still, not a player */
                          <HLSVideoPlayer
                            source={item.video}
                            autoPlay
                            muted
                            loop
                            playsInline
                            poster={item?.video?.thumbnail?.src}
                            className="absolute left-0 top-0 !h-full !w-full object-cover"
                          />
                        ) : (
                          <Image
                            className="absolute left-0 top-0 !h-full !w-full object-cover"
                            src={item.image}
                            alt=""
                          />
                        )}
                      </div>
                    )}

                    <div className="flex flex-col items-start gap-[2.25rem] sm:gap-[1.5rem]">
                      {item?.title && (
                        <h3 className="mb-0 font-geist !text-[2.5rem] font-medium leading-[1.2] text-black [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] sm:!text-[3rem]">
                          {item.title}
                        </h3>
                      )}

                      {item?.text && (
                        <p className="mb-0 w-[min(47.125rem,100%)] font-geist text-[2.25rem] leading-[1.3] text-black/50 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                          {item.text}
                        </p>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        <CarouselArrows
          className="mt-[4.125rem] w-full justify-between sm:hidden"
          api={api}
          canPrev={canPrev}
          canNext={canNext}
        />
      </div>

      {/* mobile closes the section with a second rule under the controls */}
      <div className="relative mx-auto mt-[4.125rem] h-[2px]">
        <DecorationLine
          className="top-0"
          orientation="horizontal"
          style="dark"
        />
      </div>
    </section>
  );
}
